import flatMap from 'lodash/flatMap'
import uniq from 'lodash/uniq'
import type moment from 'moment'

import { convertDate, dateToString, stringToDateWithTimezone } from 'modules/core/lib/dates'

import { View } from './types'

export interface RestDayParams {
  employeeId: string
  date: string
}

/**
 * Given an employee and a list of days, build the parameters for the rest days we
 * want to create on each one of the days.
 */
const buildRestDayParameters = (employeeId: string, days: moment.Moment[]): RestDayParams[] => {
  return days.flatMap((date: moment.Moment): RestDayParams[] => {
    const attributes: RestDayParams = {
      employeeId,
      date: dateToString(date, 'backend_datetime'),
    }

    return [{ ...attributes }]
  })
}

/**
 * Builds the map parameters to be sent when bulk creating rest days for multiple
 * employees on multiple days an  with the same start/end times.
 *
 * @param {{[key: string]: Array<string>}} daysByRow a map of the days
 *   in which we're adding rest days to each employee. The list of days is an
 *   array with using `BACKEND` format (check `lib/dates`)
 * @returns {Array<RestDayParams>}
 */
const buildAddRestDaysParameters = (
  daysByRow: Record<string, string[]>,
  view: View
): RestDayParams[] => {
  const employeeIds = Object.keys(daysByRow)
  return flatMap(employeeIds, (employeeId) => {
    const employeeIdParsed = employeeId
    const selectedDays = daysByRow[employeeId] ?? []
    // Cast the date to backend format to avoid time for rest days in daily view
    const dates = uniq(selectedDays.map((day) => convertDate(day, view.dateFormat, 'backend')))
    const employeeDates = dates.map((date) => stringToDateWithTimezone(date, 'backend', 'Etc/UTC'))

    return buildRestDayParameters(employeeIdParsed, employeeDates)
  })
}

export default buildAddRestDaysParameters
